/* Nuvō cart drawer: reliable removal, golden-hour checkout, suggestions. See snippets/nuvo-cart.liquid.

   Removal: the theme's own handler (assets/cart-sidebar.js) removes by line number and blocks the whole list while
   each request is out, so quick taps on several X's were silently dropped. Here a tap on Remove is taken before the
   theme sees it: the line slides away at once, the request is queued by the line's cart key (keys don't shift when
   other lines go), and the drawer is redrawn once from the last answer, after the queue empties. */
(() => {
  const sidebar = document.getElementById('cart-sidebar');
  const SECTIONS = ['cart-icon-bubble', 'cart-sidebar-items', 'cart-sidebar-footer', 'cart-sidebar-shipping-bar'];
  const changeUrl = ((window.routes && window.routes.cart_change_url) || '/cart/change') + '.js';

  /* Jost Light for the drawer title, the same face as the arch moments' hooks */
  const jost = () => {
    if (document.querySelector('link[href*="family=Jost"]')) return;
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Jost:wght@300&display=swap';
    document.head.appendChild(l);
  };

  /* ---------- removal ---------- */
  let queue = Promise.resolve();
  let pending = 0;
  let last = null;
  let failed = false;

  const collapse = (row) => {
    row.classList.add('is-removing');
    setTimeout(() => {
      row.style.height = row.offsetHeight + 'px';
      row.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        row.style.height = '0px';
        row.style.paddingTop = row.style.paddingBottom = '0px';
        row.style.borderBottomWidth = '0px';
      });
    }, 200);
  };

  const settle = () => {
    sidebar.classList.remove('nc-busy');
    if (!failed && last && last.sections) sidebar.updateSections(last.sections);
    else if (sidebar.reload) sidebar.reload();
    last = null;
    failed = false;
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('#cart-sidebar .cart-item__remove');
    if (!btn || !sidebar) return;
    e.preventDefault();
    e.stopPropagation();
    const row = btn.closest('.cart-sidebar__item');
    const key = btn.dataset.key || (row && row.dataset.key);
    if (!row || !key || row.classList.contains('is-removing')) return;

    collapse(row);
    pending++;
    sidebar.classList.add('nc-busy');
    queue = queue
      .then(() => fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0, sections: SECTIONS, sections_url: location.pathname }),
      }))
      .then((r) => r.json())
      .then((res) => { if (res && !res.status) last = res; else failed = true; })
      .catch(() => { failed = true; })
      .then(() => { if (--pending === 0) settle(); });
  }, true);

  /* ---------- quantity: queued, reads the live value ----------
     The theme's stepper ignores every tap while a request is out, and its links carry the quantity from when the
     line was drawn, so quick taps were lost (5 torches, four taps on minus, landed on 4). Here the box answers each
     tap at once, the last value per line (by cart key) wins, and requests go one at a time after a short pause.
     The Liquid hrefs stay as the no-JS fallback. Patch from Christian, Nov 1 2026. */
  const QDELAY = 350;
  const want = new Map();
  let qTimer = null;
  let qBusy = false;

  const rowFor = (key) => sidebar && [...sidebar.querySelectorAll('.cart-sidebar__item')].find((r) => r.dataset.key === key);

  /* at 1 the minus has nothing to do: show it, instead of ignoring the tap */
  const syncSteppers = () => {
    if (!sidebar) return;
    sidebar.querySelectorAll('.cart-sidebar__item').forEach((row) => {
      const input = row.querySelector('.quantity__input');
      const minus = row.querySelector('[aria-label="quantity-minus"]');
      if (input && minus) minus.classList.toggle('is-off', (parseInt(input.value, 10) || 0) <= 1);
    });
  };

  /* a redraw from an earlier answer must not undo taps still waiting in the queue */
  const showPending = () => want.forEach((q, key) => {
    const input = rowFor(key) && rowFor(key).querySelector('.quantity__input');
    if (input) input.value = String(q);
  });

  const flushQty = () => {
    if (qBusy || !want.size) return;
    const [key, quantity] = want.entries().next().value;
    want.delete(key);
    qBusy = true;
    sidebar.classList.add('updating');
    fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity, sections: SECTIONS, sections_url: location.pathname }),
    })
      .then((r) => r.json())
      .then((res) => { if (res && res.sections) sidebar.updateSections(res.sections); else if (sidebar.reload) sidebar.reload(); })
      .catch(() => { if (sidebar.reload) sidebar.reload(); })
      .then(() => { qBusy = false; sidebar.classList.remove('updating'); showPending(); syncSteppers(); flushQty(); });
  };

  const nudge = (row, next) => {
    const key = row.dataset.key;
    const input = row.querySelector('.quantity__input');
    if (!key || !input) return;
    next = Math.max(0, Math.floor(next) || 0);
    input.value = String(next);
    want.set(key, next);
    syncSteppers();
    clearTimeout(qTimer);
    qTimer = setTimeout(flushQty, QDELAY);
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('#cart-sidebar .quantity__button');
    if (!btn || !sidebar || sidebar.classList.contains('nc-busy')) return;
    e.preventDefault();
    e.stopPropagation();
    const row = btn.closest('.cart-sidebar__item');
    const input = row && row.querySelector('.quantity__input');
    if (!row || !input) return;
    const step = btn.getAttribute('aria-label') === 'quantity-plus' ? 1 : -1;
    const cur = parseInt(input.value, 10) || 1;
    /* minus stops at 1; a line leaves with Remove, never by a stray tap */
    nudge(row, step < 0 ? Math.max(1, cur - 1) : cur + 1);
  }, true);

  document.addEventListener('change', (e) => {
    const input = e.target.closest && e.target.closest('#cart-sidebar .quantity__input');
    if (!input) return;
    e.stopPropagation();
    const row = input.closest('.cart-sidebar__item');
    if (row) nudge(row, parseInt(input.value, 10) || 0);
  }, true);

  /* ---------- Check out leaves in golden-hour light (assets/nuvo-arch.js) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('#cart-sidebar .cart-sidebar__checkout');
    if (!btn || !window.nuvoArch || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    e.stopPropagation();
    /* the back-from-checkout hook in assets/nuvo-arch.js listens for this note */
    try { sessionStorage.setItem('nuvo-checkout', String(Date.now())); } catch (err) {}
    if (sidebar.close) sidebar.close();
    if (!window.nuvoArch.go('/checkout')) location.href = '/checkout';
  }, true);

  /* ---------- "Complete your space": suggestions under the cart lines ----------
     Pieces that complete the one in the cart rather than compete with it: torches and bowls around a fire pit, the
     oven and refrigerator beside a grill island. Drawn from the Best Sellers collection (then the full catalog),
     in best-seller order, skipping anything already in the cart. A single-option product adds in place; anything
     with options (gas type, size, finish) opens its page, so nobody adds the wrong configuration of a made-to-order
     piece. Copy: hook 45 from the approved sheet. */
  const COMPLEMENTS = {
    'Fire Pits': ['Fire Media', 'Fire Ornaments', 'Torches', 'Fire Bowls', 'Water Features'],
    'Fire Pits & Tables': ['Fire Media', 'Fire Ornaments', 'Torches', 'Fire Bowls', 'Water Features'],
    'Fire Bowls': ['Fire Media', 'Fire Ornaments', 'Torches', 'Water Features', 'Fire Pits'],
    'Water Features': ['Fire Media', 'Fire Bowls', 'Torches', 'Fire Pits'],
    Torches: ['Fire Media', 'Fire Pits', 'Fire Bowls', 'Water Features'],
    'Fire Media': ['Fire Ornaments', 'Fire Feature Controls', 'Fire Pits', 'Fire Bowls'],
    'Fire Ornaments': ['Fire Media', 'Fire Feature Controls', 'Fire Pits'],
    'Fire Feature Controls': ['Fire Media', 'Fire Ornaments', 'Fire Pits'],
    'Outdoor Kitchens': ['Outdoor Kitchen Accessories', 'Outdoor Grills', 'Outdoor Kitchens'],
    'Outdoor Grills': ['Outdoor Kitchens', 'Outdoor Kitchen Accessories'],
    'Outdoor Kitchen Accessories': ['Outdoor Kitchens', 'Outdoor Grills'],
  };
  const FIRE_LEADS = ['Fire Pits', 'Fire Pits & Tables', 'Fire Bowls'];
  const ACCESSORY_TYPES = ['Fire Media', 'Fire Ornaments', 'Fire Feature Controls'];
  const items = document.getElementById('cart-sidebar-items');
  const recs = document.createElement('div');
  recs.className = 'nc-recs';
  recs.innerHTML = '<p class="nc-recs__title">Complete your space</p>' +
    '<p class="nc-recs__sub">Everything you need to finish the space, in one place.</p>' +
    '<div class="nc-recs__track"></div>';
  const track = recs.querySelector('.nc-recs__track');
  const cache = {};
  const title = (t) => t.split(' | ')[0];
  const img = (src) => src ? (src.startsWith('//') ? 'https:' + src : src) + (src.includes('?') ? '&' : '?') + 'width=200' : '';
  const money = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD', maximumFractionDigits: 0 }).format(cents / 100);

  const cardFor = (p) => {
    const single = p.variants.length === 1 && p.variants[0].available;
    const el = document.createElement(single ? 'div' : 'a');
    el.className = 'nc-rec';
    if (!single) el.href = p.url;
    el.innerHTML = '<span class="nc-rec__img"><img alt="" loading="lazy" width="72" height="72"></span>' +
      '<span class="nc-rec__body"><b></b><small></small>' +
      (single ? '<button type="button" class="nc-rec__cta">Add</button>' : '<span class="nc-rec__cta">View</span>') + '</span>';
    const pic = el.querySelector('img');
    if (p.featured_image) pic.src = img(p.featured_image); else pic.remove();
    el.querySelector('b').textContent = title(p.title);
    el.querySelector('small').textContent = (p.price_varies ? 'From ' : '') + money(p.price_min || p.price);
    if (single) {
      el.querySelector('button').addEventListener('click', (e) => {
        const b = e.currentTarget;
        b.disabled = true;
        b.textContent = 'Adding';
        fetch('/cart/add.js', { method: 'POST', nuvoQuiet: true, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: p.variants[0].id, quantity: 1 }) })
          .then((r) => { if (!r.ok) throw new Error(r.status); return sidebar.reload(); })
          .catch(() => { b.disabled = false; b.textContent = 'Add'; });
      });
    }
    return el;
  };

  /* collection products.json, reshaped to what cardFor reads */
  const pool = async (handle) => {
    if (cache[handle]) return cache[handle];
    const r = await fetch(`/collections/${handle}/products.json?limit=250`, { headers: { Accept: 'application/json' } });
    const list = ((await r.json()).products || []).map((p) => {
      const prices = p.variants.map((v) => Math.round(parseFloat(v.price) * 100));
      return {
        id: p.id, title: p.title, type: p.product_type, url: '/products/' + p.handle,
        featured_image: p.images[0] && p.images[0].src, tags: p.tags || [],
        variants: p.variants, available: p.variants.some((v) => v.available),
        price_min: Math.min(...prices), price_varies: new Set(prices).size > 1,
      };
    });
    return (cache[handle] = list);
  };

  const productIds = () => [...items.querySelectorAll('.cart-sidebar__item[data-product-id]')].map((r) => +r.dataset.productId);
  let shownFor = null;
  const placeRecs = async () => {
    if (!items) return;
    const inCart = productIds();
    if (!inCart.length) { recs.remove(); shownFor = null; return; }
    if (!items.contains(recs) && track.childElementCount) items.appendChild(recs);
    const key = inCart.join(',');
    if (key === shownFor) return;
    shownFor = key;
    let picks = [];
    try {
      /* the most valuable piece in the cart decides what completes it */
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const lead = cart.items.reduce((a, b) => (b.final_line_price > a.final_line_price ? b : a));
      let wanted = COMPLEMENTS[lead.product_type] || [];
      const seen = new Set(inCart);
      /* a fire pit, table or bowl leads with what finishes it: a fire glass, a pebble or lava stone, an ornament,
         and the remote only when the chosen ignition is electronic (it doesn't pair with match-lit pieces) */
      if (FIRE_LEADS.includes(lead.product_type)) {
        const acc = (await pool('fire-media-accessories')).filter((p) => p.available && !seen.has(p.id) && !inCart.includes(p.id));
        const has = (p, t) => p.tags.includes(t);
        const ignition = (lead.options_with_values || []).find((o) => /ignition/i.test(o.name));
        const kinds = [
          (p) => has(p, 'Fire Glass'),
          (p) => has(p, 'Fire Pebbles') || has(p, 'Lava Rock'),
          (p) => p.type === 'Fire Ornaments',
        ];
        if (ignition && /electronic|low voltage|plug & play/i.test(ignition.value)) kinds.push((p) => p.type === 'Fire Feature Controls');
        kinds.forEach((k) => { const p = acc.find((q) => k(q) && !seen.has(q.id)); if (p) { picks.push(p); seen.add(p.id); } });
        wanted = wanted.filter((t) => !ACCESSORY_TYPES.includes(t));
      }
      for (const handle of ['best-sellers', 'all']) {
        if (picks.length >= 4) break;
        const list = (await pool(handle)).filter((p) => p.available && !seen.has(p.id) && p.type !== lead.product_type && (!wanted.length || wanted.includes(p.type)));
        /* one of each complementary type in turn (a torch, a bowl, a water feature...), best sellers first within each */
        const groups = wanted.length ? wanted.map((t) => list.filter((p) => p.type === t)) : [list];
        for (let i = 0; picks.length < 4 && groups.some((g) => g.length); i++) {
          const p = groups[i % groups.length].shift();
          if (p && !seen.has(p.id)) { picks.push(p); seen.add(p.id); }
        }
        if (picks.length >= 4) break;
      }
    } catch (err) { picks = []; }
    track.replaceChildren(...picks.map(cardFor));
    if (picks.length) items.appendChild(recs); else recs.remove();
  };
  if (items && sidebar) {
    /* the theme rewrites the lines after every change; put the suggestions back underneath */
    new MutationObserver(() => { showPending(); syncSteppers(); if (!items.contains(recs) || items.lastElementChild !== recs) placeRecs(); }).observe(items, { childList: true });
    new MutationObserver(() => { if (sidebar.dataset.status === 'open') placeRecs(); }).observe(sidebar, { attributes: true, attributeFilter: ['data-status'] });
  }

  /* ---------- the item count beside "Your cart" ----------
     The theme redraws #cart-icon-bubble after every add, change and removal; its count is the source of truth. */
  const setCount = (n) => {
    const heading = sidebar && sidebar.querySelector('.cart-sidebar__header .h3');
    if (!heading) return;
    let sup = heading.querySelector('.nc-title-count');
    if (!sup) { sup = document.createElement('sup'); sup.className = 'nc-title-count'; heading.appendChild(sup); }
    sup.textContent = n > 0 ? String(n) : '';
  };
  const readBubble = () => {
    const el = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    return el ? parseInt(el.textContent, 10) || 0 : null;
  };
  const bubble = document.getElementById('cart-icon-bubble');
  if (bubble) new MutationObserver(() => { const n = readBubble(); if (n !== null) setCount(n); }).observe(bubble, { childList: true, subtree: true, characterData: true });
  setCount(readBubble() || window.__ncCount || 0);

  syncSteppers();

  if (sidebar) {
    if ('requestIdleCallback' in window) requestIdleCallback(jost, { timeout: 3000 });
    else setTimeout(jost, 1500);
  }
})();
